import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight,
  ShieldCheck,
  Clock,
  Award,
  MapPin,
  Heart,
  Users,
  Home as HomeIcon,
  Stethoscope,
  Sun,
  UserCheck,
  HandHeart,
  HeartPulse,
  Building2,
  BedDouble,
  Star,
} from "lucide-react";
import SEO from "../components/SEO";
import { SERVICES, PROVINCES } from "../data/content";
import { organizationSchema, localBusinessSchema, breadcrumbSchema } from "../lib/jsonLd";

const SERVICE_ICONS = {
  "home-care-for-seniors": HomeIcon,
  "dementia-care": Heart,
  "palliative-care-support": HandHeart,
  "disability-support-services": Users,
  "hospital-discharge-support": HeartPulse,
  "live-in-caregivers": UserCheck,
  "staffing-for-long-term-care-homes": Building2,
  "retirement-homes": BedDouble,
  "group-homes": Users,
  "private-nursing": Stethoscope,
};

export default function Home() {
  return (
    <div data-testid="page-home">
      <SEO
        title="Home Care & Healthcare Staffing in Canada | Xtreme Care"
        description="Home care and healthcare staffing across Canada. Ask about care plans for families and staffing for facilities."
        path="/"
        keywords={[
          "home care Canada",
          "healthcare staffing Canada",
          "in-home care for seniors",
          "home care agency Ontario",
          "nurse staffing agency Canada",
        ]}
        image="https://images.unsplash.com/photo-1765896387387-0538bc9f997e"
        jsonLd={[
          organizationSchema(),
          localBusinessSchema(),
          breadcrumbSchema([{ name: "Home", path: "/" }]),
        ]}
      />

      {/* Hero */}
      <section className="bg-bg" data-testid="hero">
        <div className="site-container py-16 md:py-24 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <motion.span
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="eyebrow"
              data-testid="hero-eyebrow"
            >
              Nationwide Canada · Since 2015
            </motion.span>
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.05 }}
              className="font-serif text-4xl md:text-5xl lg:text-6xl mt-4"
              data-testid="hero-title"
            >
              Home care and healthcare staffing
              <br />
              <span className="italic text-accent">across Canada.</span>
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.15 }}
              className="mt-6 text-base md:text-lg text-ink/80 max-w-2xl leading-relaxed"
            >
              A national home-care and healthcare staffing partner with roots in
              Thunder Bay and reach across every Canadian Province and Territory.
              PSWs, DSWs and licensed nurses — for families, retirement homes,
              hospitals, and long-term care.
            </motion.p>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.35 }}
              className="mt-8 flex flex-wrap items-center gap-3"
            >
              <Link to="/contact" className="btn btn-primary btn-lg" data-testid="hero-cta-care">
                Request Care <ArrowRight className="w-4 h-4" strokeWidth={2} />
              </Link>
              <Link to="/services" className="btn btn-ghost btn-lg" data-testid="hero-cta-services">
                Explore Our Services
              </Link>
            </motion.div>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.55 }}
              className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-sm text-ink/75"
              data-testid="hero-badges"
            >
              <span className="inline-flex items-center gap-1.5"><ShieldCheck className="w-4 h-4 text-accent" strokeWidth={1.75} /> Bonded &amp; insured</span>
              <span className="inline-flex items-center gap-1.5"><Clock className="w-4 h-4 text-accent" strokeWidth={1.75} /> 24/7 coordination</span>
              <span className="inline-flex items-center gap-1.5"><Award className="w-4 h-4 text-accent" strokeWidth={1.75} /> Licensed nurses on staff</span>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="lg:col-span-5"
          >
            <div className="bg-white border border-ink/10 rounded-md overflow-hidden shadow-sm">
              <div className="h-1.5 bg-accent" />
              <img
                src="https://images.unsplash.com/photo-1765896387387-0538bc9f997e"
                alt="A PSW helping a senior at home"
                className="w-full h-72 object-cover"
                data-testid="hero-image"
              />
              <div className="p-6">
                <p className="font-serif text-lg italic text-ink/85">
                  &ldquo;They noticed the small things the hospital had stopped noticing.&rdquo;
                </p>
                <p className="mt-3 text-sm text-ink/60">Anwar F. · Ottawa · family of a client</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Stats strip */}
      <section className="bg-dark text-bg" data-testid="stats-strip">
        <div className="site-container py-10 md:py-14 grid grid-cols-2 md:grid-cols-4 gap-6">
          {[
            { v: "24/7", l: "Care coordination" },
            { v: "13", l: "Provinces & territories" },
            { v: "241+", l: "Cities served nationwide" },
            { v: "2,400+", l: "Households cared for" },
          ].map((s) => (
            <div key={s.l} className="text-center" data-testid={`stat-${s.v}`}>
              <div className="font-serif text-4xl md:text-5xl text-accent">{s.v}</div>
              <div className="mt-2 uppercase text-xs tracking-widest text-bg/80">{s.l}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Services grid */}
      <section className="bg-bg" data-testid="services-preview">
        <div className="site-container py-16 md:py-24">
          <div className="flex items-end justify-between gap-4 flex-wrap">
            <div>
              <span className="eyebrow">What we do</span>
              <h2 className="font-serif text-3xl md:text-4xl mt-3">A full spectrum of care services.</h2>
            </div>
            <Link to="/services" className="inline-flex items-center gap-1.5 text-accent font-semibold text-sm hover:underline" data-testid="services-see-all">
              All services <ArrowRight className="w-4 h-4" strokeWidth={2} />
            </Link>
          </div>

          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            {SERVICES.map((s) => {
              const Icon = SERVICE_ICONS[s.slug] || Heart;
              return (
                <Link
                  key={s.slug}
                  to={`/services/${s.slug}`}
                  className="card group flex flex-col"
                  data-testid={`home-service-${s.slug}`}
                >
                  <div className="w-11 h-11 rounded-full bg-surface flex items-center justify-center">
                    <Icon className="w-5 h-5 text-accent" strokeWidth={1.75} />
                  </div>
                  <h3 className="font-serif text-xl mt-4">{s.title}</h3>
                  <p className="mt-2 text-sm text-ink/70 flex-1">{s.tagline}</p>
                  <span className="mt-5 inline-flex items-center gap-1.5 text-accent text-sm font-semibold">
                    Learn more <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" strokeWidth={2} />
                  </span>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why us */}
      <section className="bg-surface" data-testid="why-section">
        <div className="site-container py-16 md:py-24 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7">
            <span className="eyebrow">Why Xtreme Care</span>
            <h2 className="font-serif text-3xl md:text-4xl mt-3">
              Community-first values. National-grade discipline.
            </h2>
            <p className="mt-5 text-ink/80 max-w-2xl leading-relaxed">
              We are the care partner families choose when they need more than a
              uniform — when they need continuity of relationship, licensed
              nursing oversight, and a company whose word means something.
            </p>
            <ul className="mt-8 space-y-3.5">
              {[
                ["Bonded, insured &amp; vetted.", "Vulnerable-sector cleared PSWs with current provincial credentials."],
                ["A rotation, not a stranger.", "Two to three primary caregivers per household — no revolving door."],
                ["Registered nurses author every plan.", "Care plans built by an RN, reviewed monthly by a supervisor."],
                ["Nationwide reach.", "Operating in every Canadian province and territory across 241+ cities."],
                ["Same-shift facility fill above 96%.", "Because our staffing bench is over-supplied against seasonal demand."],
                ["24-hour deployment window.", "In most Canadian cities we can begin service within one day."],
              ].map(([title, body]) => (
                <li key={title} className="pl-4 border-l-2 border-accent" data-testid={`why-item-${title.slice(0, 12)}`}>
                  <span
                    className="font-semibold text-ink"
                    dangerouslySetInnerHTML={{ __html: title }}
                  />{" "}
                  <span className="text-ink/75">{body}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="lg:col-span-5">
            <div className="card p-6 md:p-8" data-testid="why-highlight">
              <MapPin className="w-6 h-6 text-accent" strokeWidth={1.75} />
              <h3 className="font-serif text-2xl mt-4">Headquartered in Thunder Bay, Ontario</h3>
              <p className="mt-3 text-ink/75 leading-relaxed">
                Our Northwestern Ontario base gives us fast response across
                Northern Ontario and Manitoba, while our licensed partner
                network extends coverage to every Canadian province and
                territory.
              </p>
              <div className="mt-6 pt-6 border-t border-ink/10">
                <div className="flex items-baseline gap-3">
                  <span className="font-serif text-4xl text-accent">241+</span>
                  <span className="text-sm text-ink/70">Cities served across Canada</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="bg-bg" data-testid="testimonials-section">
        <div className="site-container py-16 md:py-24">
          <span className="eyebrow">Client voices</span>
          <h2 className="font-serif text-3xl md:text-4xl mt-3">Trusted by families, facilities, and communities.</h2>

          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-5">
            {[
              {
                q: "Professional, punctual and detail-oriented — a genuine extension of our operations team.",
                who: "Site Operations Lead",
                where: "Long-term care home, Alberta",
              },
              {
                q: "The same PSW every week. My mother knows her name. That has changed our lives.",
                who: "Family caregiver",
                where: "Toronto, ON",
              },
              {
                q: "Verifiable proof of every shift — exactly what a national facilities team needs.",
                who: "Building Services Manager",
                where: "National retail, multi-city",
              },
            ].map((t, i) => (
              <blockquote key={i} className="card" data-testid={`testimonial-${i}`}>
                <div className="flex items-center gap-1 text-accent">
                  {Array.from({ length: 5 }).map((_, k) => (
                    <Star key={k} className="w-4 h-4 fill-accent" strokeWidth={0} />
                  ))}
                </div>
                <p className="mt-4 font-serif text-lg leading-snug text-ink">&ldquo;{t.q}&rdquo;</p>
                <div className="mt-5 pt-4 border-t border-ink/10">
                  <div className="font-semibold text-sm">{t.who}</div>
                  <div className="text-sm text-ink/60">{t.where}</div>
                </div>
              </blockquote>
            ))}
          </div>
        </div>
      </section>

      {/* Locations dark section */}
      <section className="bg-dark text-bg" data-testid="locations-preview">
        <div className="site-container py-16 md:py-24">
          <div className="flex items-end justify-between gap-4 flex-wrap">
            <div>
              <span className="eyebrow eyebrow-light">Coast-to-coast coverage</span>
              <h2 className="font-serif text-3xl md:text-4xl mt-3">Local caregivers. National standards.</h2>
            </div>
            <Link to="/locations" className="inline-flex items-center gap-1.5 text-accent font-semibold text-sm hover:underline" data-testid="locations-see-all">
              All locations <ArrowRight className="w-4 h-4" strokeWidth={2} />
            </Link>
          </div>

          {/* Featured cities */}
          <div className="mt-10 flex flex-wrap gap-2 md:gap-2.5">
            {["Toronto", "Ottawa", "Vancouver", "Calgary", "Edmonton", "Montréal", "Winnipeg", "Halifax", "Mississauga", "Hamilton", "Kitchener", "Victoria", "London", "Regina", "Saskatoon", "St. John's", "Yellowknife", "Whitehorse"].map((c) => {
              // Find its province
              const prov = PROVINCES.find((p) => p.cities.includes(c));
              if (!prov) return null;
              const citySlug = c
                .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
                .toLowerCase().replace(/[.']/g, "").replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
              return (
                <Link
                  key={c}
                  to={`/locations/${prov.code.toLowerCase()}/${citySlug}`}
                  className="chip"
                  data-testid={`loc-chip-${citySlug}`}
                >
                  <span className="font-medium">{c}</span>
                  <span className="text-bg/60 text-[10px] uppercase tracking-widest">{prov.code}</span>
                </Link>
              );
            })}
          </div>

          <div className="mt-12 pt-8 border-t border-bg/15 grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-3">
            {PROVINCES.map((p) => (
              <Link
                key={p.code}
                to={`/locations/${p.code.toLowerCase()}`}
                className="flex items-baseline justify-between py-2.5 border-b border-bg/10 hover:border-accent group"
                data-testid={`home-province-${p.code}`}
              >
                <span className="font-serif text-lg group-hover:text-accent transition-colors">{p.name}</span>
                <span className="text-bg/50 text-xs tracking-widest group-hover:text-accent">{p.code}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA banner */}
      <section className="bg-accent text-ink" data-testid="home-cta">
        <div className="site-container py-14 md:py-16 flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <h2 className="font-serif text-3xl md:text-4xl">Ready to talk care?</h2>
            <p className="mt-3 max-w-xl text-ink/85">
              Tell us about your loved one, your facility, or your programme. A
              senior coordinator will respond with a tailored proposal — usually
              within one business day.
            </p>
          </div>
          <Link to="/contact" className="btn btn-dark btn-lg shrink-0" data-testid="cta-request">
            Request Your Consultation <ArrowRight className="w-4 h-4" strokeWidth={2} />
          </Link>
        </div>
      </section>

      {/* Provinces dashboard bar — moved to bottom of the home page */}
      <section className="bg-surface border-t border-ink/10" data-testid="provinces-bar">
        <div className="site-container py-8 md:py-10">
          <div className="flex items-center gap-3 mb-5">
            <Sun className="w-4 h-4 text-accent" strokeWidth={2} />
            <span className="eyebrow">Serving all of Canada — jump to a region</span>
          </div>
          <div className="flex flex-wrap gap-1.5 md:gap-2">
            {PROVINCES.map((p) => (
              <Link
                key={p.code}
                to={`/locations/${p.code.toLowerCase()}`}
                className="chip chip-light text-xs px-3 py-1.5"
                data-testid={`province-chip-${p.code}`}
                title={p.name}
              >
                <span className="font-semibold">{p.code}</span>
                <span className="text-ink/60">{p.name}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
